import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n";
import { HERO_IMAGE } from "@/data/projects";

const EASE = [0.22, 1, 0.36, 1];

function MaskedLine({ text, delay, className }) {
  return (
    <span className={`block overflow-hidden ${className || ""}`}>
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, delay, ease: EASE }}
      >
        {text}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const { lang, t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.2]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden bg-ebony" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0 scale-110">
        <img src={HERO_IMAGE} alt="Shanzu architecture" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-black/40" />
      <div className="absolute inset-0 pattern-kitenge opacity-60" />

      <motion.div style={{ opacity }} className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-12 flex flex-col justify-end pb-24 md:pb-32">
        <MaskedLine
          text={t.hero.overline}
          delay={0.2}
          className="text-xs md:text-sm uppercase tracking-[0.3em] text-ochre mb-6 font-sans"
        />
        <h1 className="font-serif font-light text-bone text-5xl sm:text-6xl lg:text-8xl leading-[1.02] tracking-tighter" data-testid="hero-title">
          <MaskedLine text={t.hero.l1} delay={0.35} />
          <MaskedLine text={t.hero.l2} delay={0.5} />
          <span className="italic text-ochre">
            <MaskedLine text={t.hero.l3} delay={0.65} />
          </span>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.0, ease: EASE }}
          className="mt-8 max-w-xl text-bone/80 font-light text-sm md:text-base leading-relaxed"
          data-testid="hero-subtitle"
        >
          {t.hero.sub}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.15, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to={`/${lang}/#ventures`}
            data-testid="hero-cta-ventures"
            className="group inline-flex items-center gap-3 rounded-full bg-terracotta px-7 py-3.5 text-sm font-medium text-bone hover:bg-ochre hover:text-ebony transition-colors duration-500"
          >
            {t.hero.cta1}
            <ArrowRight size={16} className="transition-transform duration-500 group-hover:translate-x-1" />
          </Link>
          <Link
            to={`/${lang}/#contact`}
            data-testid="hero-cta-contact"
            className="inline-flex items-center gap-3 rounded-full border border-bone/40 px-7 py-3.5 text-sm font-light text-bone hover:border-bone hover:bg-bone/10 transition-colors duration-500"
          >
            {t.hero.cta2}
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 right-6 lg:right-12 z-10 hidden md:flex items-center gap-3 text-bone/60"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ArrowDown size={14} />
        </motion.span>
      </motion.div>
    </section>
  );
}
